import type { Recommendation, RecCategory, RecDifficulty } from '@lensora/shared'
import { cn } from '@lensora/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Sparkles, Zap } from 'lucide-react'
import * as React from 'react'

import { ImprovementActionCard } from '@/components/dashboard/ImprovementActionCard'
import { api } from '@/lib/api'

const CATEGORY_FILTERS: RecCategory[] = ['content', 'technical', 'authority', 'structured_data', 'entity']
const DIFFICULTY_FILTERS: RecDifficulty[] = ['easy', 'medium', 'hard']

const CATEGORY_LABELS: Record<RecCategory, string> = {
  content: 'content',
  technical: 'technical',
  authority: 'authority',
  structured_data: 'structured data',
  entity: 'entity',
}

function FilterPill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        active ? 'border-brand-accent/40 bg-brand-accent/10 text-brand-accent' : 'border-border text-text-muted hover:bg-surface-overlay'
      )}
    >
      {children}
    </button>
  )
}

export function ImprovementActionsSection({ websiteId }: { websiteId: string | null }) {
  const queryClient = useQueryClient()
  const [categoryFilter, setCategoryFilter] = React.useState<RecCategory | 'all'>('all')
  const [difficultyFilter, setDifficultyFilter] = React.useState<RecDifficulty | 'all'>('all')

  const { data: recommendations, isLoading } = useQuery({
    queryKey: ['recommendations', websiteId],
    queryFn: () => api.get<Recommendation[]>(`/api/recommendations?website_id=${websiteId}`),
    enabled: !!websiteId,
    staleTime: 30_000,
  })

  const { mutate: updateStatus, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'completed' | 'dismissed' }) =>
      api.patch(`/api/recommendations/${id}`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recommendations', websiteId] })
    },
  })

  const filtered = React.useMemo(() => {
    const list = recommendations ?? []
    return list
      .filter((r) => r.status !== 'dismissed')
      .filter((r) => categoryFilter === 'all' || r.category === categoryFilter)
      .filter((r) => difficultyFilter === 'all' || r.difficulty === difficultyFilter)
      .sort((a, b) => b.estimatedImpact - a.estimatedImpact)
  }, [recommendations, categoryFilter, difficultyFilter])

  const hasAnyRecommendations = (recommendations ?? []).length > 0

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-bold text-text">
            <Zap className="h-5 w-5 text-brand-accent" />
            Improvement Actions
            <span className="text-sm font-normal text-text-muted">Ranked by impact</span>
          </h2>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-48 animate-pulse rounded-lg border border-border bg-surface" />
          ))}
        </div>
      ) : !hasAnyRecommendations ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-surface py-16 text-center">
          <Sparkles className="mb-4 h-12 w-12 text-text-muted opacity-40" />
          <p className="font-semibold text-text">No improvement actions yet</p>
          <p className="mt-1 max-w-sm text-sm text-text-muted">
            Run a website audit crawl to generate personalized recommendations for content, technical SEO, and structured data.
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <FilterPill active={categoryFilter === 'all'} onClick={() => setCategoryFilter('all')}>All</FilterPill>
            {CATEGORY_FILTERS.map((c) => (
              <FilterPill key={c} active={categoryFilter === c} onClick={() => setCategoryFilter(c)}>
                {CATEGORY_LABELS[c]}
              </FilterPill>
            ))}
            <span className="mx-1 h-4 w-px bg-border" />
            <FilterPill active={difficultyFilter === 'all'} onClick={() => setDifficultyFilter('all')}>All difficulty</FilterPill>
            {DIFFICULTY_FILTERS.map((d) => (
              <FilterPill key={d} active={difficultyFilter === d} onClick={() => setDifficultyFilter(d)}>
                {d}
              </FilterPill>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {filtered.map((rec) => (
              <ImprovementActionCard
                key={rec.id}
                recommendation={rec}
                isUpdating={isUpdating}
                onComplete={(id) => updateStatus({ id, status: 'completed' })}
                onDismiss={(id) => updateStatus({ id, status: 'dismissed' })}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
